/* eslint-disable no-param-reassign */
import clamp from 'lodash/clamp';
import { ARROW_PORT_OFFSET } from './ellipsis.tsx';
import type { Point, PortLayout } from './toPortInfo.tsx';
import { move, line, combinePath } from './lineLeft.tsx';
import { type Vector, axisOf, extractPoint } from './Axis.types.tsx';
import {
	distanceAll,
	pointOnPathByDistance,
	coincides,
	CONNECTION_ENDS_PADDING,
	turnDirection,
	displace,
	inFrontOf,
	hasSharedAxis,
	portIndexToVector,
} from './inFrontOf.tsx';

/**
 * To prevent routing getting stuck in an infinite loop for whatever reason, we only perform this many iterations of each step.
 */
const MAX_ITERATIONS = 4;

const analyzeVectors = (subject: Vector, other: Vector) => {
	const doesSubjectFaceOther = inFrontOf(subject, other);
	const doesOtherFaceSubject = inFrontOf(other, subject);
	const subjectAxis = axisOf(subject);
	const otherAxis = axisOf(other);

	const distanceBetween = distanceAll(extractPoint(subject), extractPoint(other));

	const isFacingEachOtherOnSameLine =
		doesSubjectFaceOther && doesOtherFaceSubject && hasSharedAxis(subject, other);
	const isFacingEachOtherInParallel =
		doesSubjectFaceOther && doesOtherFaceSubject && subjectAxis === otherAxis;

	return {
		doesSubjectFaceOther,
		doesOtherFaceSubject,
		distanceBetween,
		subjectAxis,
		otherAxis,
		isFacingEachOtherOnSameLine,
		isFacingEachOtherInParallel,
	} as const;
};

/**
 * Performs a step in the manhattan routing given a 'subject' vector and 'other' vector to go towards.
 *
 * Logic is as follows
 * 1. if both facing each 'other' on same line, they can meet
 * 2. otherwise if both facing each 'other' but in parallel, they walk half way on that axis
 * 3. otherwise if subject faces 'other', move subject closest to 'other' in one axis
 * 4. otherwise subject has to turn towards 'other'
 *
 * See the [visualization of the steps](https://hello.atlassian.net/wiki/spaces/HELIX/pages/2821877619/Manhattan+Algorithm+-+React+Workflow+Diagram) for more info
 * @param subject the current 'subject' vector being considered
 * @param other the other vector (i.e. not the 'subject')
 * @returns
 */
function mutativelyPlotTo(subject: Vector, other: Vector): Point | undefined {
	const {
		doesSubjectFaceOther,
		doesOtherFaceSubject,
		subjectAxis,
		otherAxis,
		isFacingEachOtherOnSameLine,
		isFacingEachOtherInParallel,
	} = analyzeVectors(subject, other);

	// subject →      ← other
	if (isFacingEachOtherOnSameLine) {
		subject[otherAxis] = other[otherAxis];
		return extractPoint(subject);
	}

	// subject →
	//            ← other
	if (isFacingEachOtherInParallel) {
		const middle = (other[otherAxis] - subject[subjectAxis]) / 2;

		const updatedSubject = displace(subject, middle);
		subject.x = updatedSubject.x;
		subject.y = updatedSubject.y;

		const updatedOther = displace(other, middle);
		other.x = updatedOther.x;
		other.y = updatedOther.y;

		return undefined;
	}

	// subject →
	//             •
	//           other
	if (doesSubjectFaceOther) {
		// subject →
		//           other
		//             ↓
		if (!doesOtherFaceSubject) {
			return undefined;
		}

		// subject →
		//             ↑
		//           other
		subject[subjectAxis] = other[subjectAxis];
		return extractPoint(subject);
	}

	// ← subject
	//             •
	//           other
	const direction = turnDirection(subject, other);

	if (!direction) {
		return undefined;
	}

	subject.direction = direction;
	return extractPoint(subject);
}

/**
 * Recreation of the Manhattan routing from the original draw2d Workflow Diagram.
 * Returns an array of Points that plot the path.
 *
 * Please see the [accompanying documentation](https://hello.atlassian.net/wiki/spaces/HELIX/pages/2821877619) for visual walkthrough
 * and to understand the comments better.
 */
export function plotPoints(start: Vector, end: Vector): Point[] {
	const alice = start;
	const fiona = end;

	// points starting at `start` and continuing to middle
	const leadingPoints = [extractPoint(alice)];
	// points starting at middle and ending at `end`
	const trailingPoints = [extractPoint(fiona)];

	// keep moving until both meet, but break after MAX_ITERATIONS
	// times to avoid infinite loop
	for (let i = 0; i < MAX_ITERATIONS; i += 1) {
		if (coincides(alice, fiona)) break;

		const leadingPoint = mutativelyPlotTo(alice, fiona);
		if (leadingPoint) leadingPoints.push(leadingPoint);

		const trailingPoint = mutativelyPlotTo(fiona, alice);
		if (trailingPoint) trailingPoints.unshift(trailingPoint);
	}

	return [...leadingPoints, ...trailingPoints];
}

const interpolate = (min: number, max: number, ratio: number) => min + ratio * (max - min);

/**
 * Displaces the start and end point away from the connected ports
 */
export const plotPointsWithOffset = (start: Vector, end: Vector) => {
	const { isFacingEachOtherInParallel, distanceBetween } = analyzeVectors(start, end);

	// Move away from ports
	const a = displace(start, ARROW_PORT_OFFSET);
	const f = displace(end, ARROW_PORT_OFFSET);

	// Move a further 'padding' distance away depending on distance between points
	let padding = CONNECTION_ENDS_PADDING;

	if (isFacingEachOtherInParallel) {
		const ratio = clamp(distanceBetween, 0, 100) / 100;
		padding = interpolate(0, CONNECTION_ENDS_PADDING, ratio);
	}

	const b = displace(a, padding);
	const e = displace(f, padding);

	return [a, ...plotPoints(b, e), f];
};

/**
 * Calculate label position (midpoint if fractionOfPath is 0.5). Different fractionOfPath values are used for overlapping transitions.
 * @param fractionOfPath fractional value between 0 to 1 describing where to find the label position on the path (default is 0.5 which is the midpoint)
 * @param padding fractional value between 0 and 1 of padding used to pad start and end of label positions can appear. (default is 0.2, which means 20% padding each for start and end)
 * @returns
 */
export const calculateLabelPosition = (path: Point[], fractionOfPath = 0.5, padding = 0.2) => {
	const totalDistance = distanceAll(...path);

	const normalisedLabelPositionBias = fractionOfPath * (1 - padding * 2) + padding;
	return pointOnPathByDistance(path, totalDistance * normalisedLabelPositionBias);
};

type LabelPositionOptions =
	| { fractionOfPath: number | undefined; endsPadding: number }
	| { skipLabelPosition: true };

export const manhattanPathAndLabelPosition = (
	startPort: PortLayout,
	endPort: PortLayout,
	labelPositionOptions: LabelPositionOptions = {
		fractionOfPath: 0.5,
		endsPadding: 0.2,
	},
) => {
	const start = portIndexToVector(startPort);
	const end = portIndexToVector(endPort);

	const points = plotPointsWithOffset(start, end);

	let labelPosition = extractPoint(start);
	if (!('skipLabelPosition' in labelPositionOptions)) {
		labelPosition = calculateLabelPosition(
			points,
			labelPositionOptions.fractionOfPath,
			labelPositionOptions.endsPadding,
		);
	}

	const [firstPoint, ...restOfPoints] = points;
	const path = combinePath(move(firstPoint), ...restOfPoints.map((point) => line(point)));

	return {
		/**
		 * SVG path data of the final route
		 */
		path,
		/**
		 * Point on the route that would be a transition label's center given `labelPositionOptions`. If none provided, returns the midpoint.
		 */
		labelPosition,
	};
};
